import { useMemo } from 'react'

import { FormattedMessage } from 'react-intl'

import type { Expense as ExpenseType, Income } from '@subaio/api-types/budget'
import { createSpinnerTabler, FormattedNumber, Placeholder } from '@subaio/components'

import { Cell, HeaderRow } from 'components/Lists'

import { ChevronFlip } from '../ChevronFlip'
import { EmptyChevron } from '../EmptyChevron'
import { Service } from '../Service'
import { Hint } from './Hint'
import { Input } from './Input'
import { ItemExpandable } from './ItemExpandable'
import { SpinnerCell } from './SpinnerCell'

const Spinner = createSpinnerTabler(({ theme }) => ({
	size: 28,
	color: theme.color.text,
	backgroundColor: theme.color.background,
}))

type Props = {
	id: string
	name: string

	currency: string
	expenses?: Array<ExpenseType>
	fieldsBeingSubmitted: Array<string>
	income?: Array<Income>
	totalMonthlyAmount: number
	totalMonthlyAmountUserProvided: number | null
}

const threeMonthsAverageCategories = ['income_salary', 'income_public_benefit', 'income_other_income']
const twelveMonthsAverageCategories = [
	'income_tax_and_yield',
	'house_utilities',
	'other_insurance',
	'other_pension',
	'other_public_expenses',
	'other_child_support',
]

export const Subcategory = ({
	id,
	name,
	currency = 'DKK',
	totalMonthlyAmount,
	totalMonthlyAmountUserProvided,
	expenses,
	income,
	fieldsBeingSubmitted,
}: Props) => {
	const hasIncome = income && income.length > 0
	const hasServices = (expenses && expenses.length > 0) || hasIncome
	const isBeingSubmitted = fieldsBeingSubmitted.includes(id)

	const amountToShow = useMemo(() => {
		if (threeMonthsAverageCategories.includes(id)) return 'threeMonthsAverage'
		if (twelveMonthsAverageCategories.includes(id)) return 'twelveMonthsAverage'
		return 'default'
	}, [id])

	return (
		<ItemExpandable
			renderRow={({ isExpanded, ...rest }) => (
				<HeaderRow {...rest}>
					<Cell textAlign='center'>{hasServices ? <ChevronFlip isActive={isExpanded} /> : <EmptyChevron />}</Cell>
					<Cell shouldExpand>{name}</Cell>
					{fieldsBeingSubmitted.includes(id) && (
						<SpinnerCell>
							<Spinner />
						</SpinnerCell>
					)}
					<Cell textAlign='right'>
						<Input
							label=''
							name={`categories.${id}.amount`}
							type='number'
							autoComplete='off'
							isDisabled={isBeingSubmitted}
							hint={
								totalMonthlyAmountUserProvided !== null && (
									<Hint>
										<Placeholder>
											{/* This is the only time we want to show `0` if that is the starting point */}
											<FormattedMessage id='subcategory.changedText' defaultMessage='Original' />{' '}
											<FormattedNumber value={totalMonthlyAmount} currency={currency} format='integer-currency' />
										</Placeholder>
									</Hint>
								)
							}
							validate={{ wholeNumber: (value) => /^\d*$/.test(value) || value === undefined }}
							renderSuffix={() => currency}
							showValidationMessage={false}
							onClick={(event) => {
								event.stopPropagation()
							}}
							onBlur={(event) => {
								if (event.currentTarget.value === '' && totalMonthlyAmountUserProvided === null) {
									event.currentTarget.value = Math.round(totalMonthlyAmount).toString()
								}
							}}
						/>
					</Cell>
				</HeaderRow>
			)}
			isDisabled={!hasServices}
		>
			{expenses?.map((expense, index) => (
				<Service key={index} amountToShow={amountToShow} {...expense} />
			))}
			{income?.map((income, index) => (
				<Service key={index} amountToShow={amountToShow} {...income} />
			))}
		</ItemExpandable>
	)
}
